import { useState, useEffect, useRef, useMemo, useCallback, Fragment } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Headphones,
  Download,
  RefreshCw,
  Play,
  Pause,
  Loader2,
  Search,
  Filter,
  Merge,
  CheckSquare,
  Square,
  FileAudio,
  X,
  ChevronDown,
  ChevronUp,
  Trash2,
  Radio,
  MessageSquare,
  Eye,
  EyeOff,
  List,
  LayoutList
} from "lucide-react";

function formatFileSize(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${month}/${day}`;
}

function formatTime(timeStr) {
  if (!timeStr) return "";
  const [hours, minutes] = timeStr.split(":");
  const h = parseInt(hours);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
}

function formatDateLong(dateStr) {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-").map(Number);
  const recDate = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (recDate.getTime() === today.getTime()) return "Today";
  if (recDate.getTime() === yesterday.getTime()) return "Yesterday";
  return recDate.toLocaleDateString("en-US", {
    weekday: recDate.getFullYear() === today.getFullYear() ? "short" : undefined,
    month: "short",
    day: "numeric",
    year: recDate.getFullYear() === today.getFullYear() ? undefined : "numeric",
  });
}

export default function RadioRecordings() {
  const { user } = useAuth();
  const [recordings, setRecordings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtering, setFiltering] = useState(false); // For filter changes (no flash)
  const [syncing, setSyncing] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeProgress, setMergeProgress] = useState(null); // { state, downloaded, total }
  const [deleting, setDeleting] = useState(false);

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterUser, setFilterUser] = useState("");
  const [filterUserType, setFilterUserType] = useState(""); // "app", "radio", or ""
  const [filterChannel, setFilterChannel] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterTimeFrom, setFilterTimeFrom] = useState("");
  const [filterTimeTo, setFilterTimeTo] = useState("");

  // Channel list
  const [channels, setChannels] = useState([]);

  // User categories
  const [appUsers, setAppUsers] = useState([]);
  const [radioUsers, setRadioUsers] = useState([]);

  // Selection state
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [visibleCount, setVisibleCount] = useState(100);
  // Server-side pagination for recordings (was: fetch all 5,000 every load +
  // re-fetch all every 5s, which saturated the DB). We page in chunks.
  const RECORDINGS_PAGE = 200;
  const [recordingsTotal, setRecordingsTotal] = useState(0);
  const [loadingMoreRecordings, setLoadingMoreRecordings] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("radio_recordings_view_mode") || "detailed"; } catch { return "detailed"; }
  });
  const [transcriptDialogFile, setTranscriptDialogFile] = useState(null);
  const [transcribingFile, setTranscribingFile] = useState(null);
  const [transcribingAll, setTranscribingAll] = useState(false);

  // Audio player state
  const [playingFile, setPlayingFile] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const audioRef = useRef(null);

  // Tab access control - requires both radio page access AND the specific role toggle
  const isAdmin = user?.role === "admin";
  const has71Access = isAdmin || user?.radio_71_access;
  const hasRecordingsAccess = isAdmin || user?.recording_admin_access;

  // Transcription (71) tab state - default to whichever tab the user has access to
  const [activeTab, setActiveTab] = useState(has71Access ? "71" : "recordings");
  const [transcriptions, setTranscriptions] = useState([]);
  const [loadingTranscriptions, setLoadingTranscriptions] = useState(false);
  const [transcriptionSearch, setTranscriptionSearch] = useState("");
  const [transcriptionTotal, setTranscriptionTotal] = useState(0);
  const [trDateFrom, setTrDateFrom] = useState("");
  const [trDateTo, setTrDateTo] = useState("");
  const [showTranscriptText, setShowTranscriptText] = useState(true);
  const [showRecordingText, setShowRecordingText] = useState(true);
  const [playingTranscription, setPlayingTranscription] = useState(null);
  const [transcriptionPlaying, setTranscriptionPlaying] = useState(false);
  const [trAudioProgress, setTrAudioProgress] = useState(0);
  const [trAudioDuration, setTrAudioDuration] = useState(0);
  const [trSelectMode, setTrSelectMode] = useState(false);
  const [trSelected, setTrSelected] = useState(new Set());
  const [trDownloading, setTrDownloading] = useState(false);
  const transcriptionAudioRef = useRef(null);
  const transcriptionBlobRef = useRef(null);
  const transcriptionFeedRef = useRef(null);
  const pollingRef = useRef(null);
  const recordingsPollRef = useRef(null);
  const userAtBottomRef = useRef(true);

  useEffect(() => {
    try { localStorage.setItem("radio_recordings_view_mode", viewMode); } catch {}
  }, [viewMode]);

  useEffect(() => {
    if (activeTab === "recordings") {
      if (recordings.length === 0) {
        fetchRecordings();
        fetchUsers();
        fetchChannels();
      }
    } else if (activeTab === "71") {
      fetchTranscriptions();
    }
  }, [activeTab]);

  // Polling for new transcriptions
  useEffect(() => {
    if (activeTab !== "71") {
      if (pollingRef.current) clearInterval(pollingRef.current);
      return;
    }
    pollingRef.current = setInterval(pollTranscriptions, 5000);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [activeTab, transcriptions]);

  // Live updates for recordings tab — pull external sync (cooldown-gated on
  // the backend) and refresh the list when new recordings appear. Skipped
  // while filters/search are active so we don't fight the user.
  useEffect(() => {
    if (recordingsPollRef.current) clearInterval(recordingsPollRef.current);
    if (activeTab !== "recordings") return;
    const hasFilter = !!(searchQuery || filterUser || filterUserType || filterChannel || filterDateFrom || filterDateTo || filterTimeFrom || filterTimeTo);
    if (hasFilter) return;
    recordingsPollRef.current = setInterval(pollRecordings, 5000);
    return () => { if (recordingsPollRef.current) clearInterval(recordingsPollRef.current); };
  }, [activeTab, searchQuery, filterUser, filterUserType, filterChannel, filterDateFrom, filterDateTo, filterTimeFrom, filterTimeTo]);

  const [loadingMore, setLoadingMore] = useState(false);

  const fetchTranscriptions = async (searchVal, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoadingTranscriptions(true);
    }
    try {
      const params = new URLSearchParams();
      params.append("limit", "200");
      if (append) params.append("skip", String(transcriptions.length));
      const s = searchVal !== undefined ? searchVal : transcriptionSearch;
      if (s) params.append("search", s);
      if (trDateFrom) params.append("date_from", trDateFrom);
      if (trDateTo) params.append("date_to", trDateTo);
      const res = await axios.get(`${API}/radio/transcriptions?${params}`, { withCredentials: true });
      if (append) {
        setTranscriptions(prev => [...prev, ...(res.data.transcriptions || [])]);
      } else {
        setTranscriptions(res.data.transcriptions || []);
      }
      setTranscriptionTotal(res.data.total || 0);
    } catch (err) {
      console.error("Failed to fetch transcriptions:", err);
    } finally {
      setLoadingTranscriptions(false);
      setLoadingMore(false);
    }
  };

  const pollTranscriptions = async () => {
    if (transcriptionSearch || trDateFrom || trDateTo) return; // Don't poll while searching or filtering by date
    const latest = transcriptions.length > 0 ? transcriptions[0]?.created_at : null;
    if (!latest) return;
    try {
      const params = new URLSearchParams();
      params.append("since", latest);
      const res = await axios.get(`${API}/radio/transcriptions/feed?${params}`, { withCredentials: true });
      const newItems = res.data.transcriptions || [];
      if (newItems.length > 0) {
        setTranscriptions(prev => [...newItems.reverse(), ...prev]);
        setTranscriptionTotal(prev => prev + newItems.length);
        // Auto-scroll if user is at top
        if (userAtBottomRef.current && transcriptionFeedRef.current) {
          setTimeout(() => {
            transcriptionFeedRef.current?.scrollTo({ top: 0 });
          }, 100);
        }
      }
    } catch (err) {
      // Silent fail for polling
    }
  };

  const pollRecordings = async () => {
    try {
      // Only fetch page 1 (newest RECORDINGS_PAGE) for new-item detection —
      // NOT the whole collection. The old poll pulled all ~5,000 docs every
      // 5s, which (per request taking ~30s) stacked up and saturated the DB,
      // dragging down the entire app. We merge any genuinely-new items onto
      // the front so previously "Load More"-d pages are preserved.
      const params = new URLSearchParams();
      params.append("limit", String(RECORDINGS_PAGE));
      params.append("skip", "0");
      const res = await axios.get(`${API}/radio/recordings?${params.toString()}`, { withCredentials: true });
      const next = res.data.recordings || [];
      if (typeof res.data.total === "number") setRecordingsTotal(res.data.total);
      setRecordings(prev => {
        // No change if the newest filename matches what we already have on top.
        if (prev[0]?.filename === next[0]?.filename) return prev;
        const seen = new Set(prev.map(r => r.filename));
        const fresh = next.filter(r => !seen.has(r.filename));
        if (fresh.length === 0) return prev;
        return [...fresh, ...prev];
      });
    } catch (err) {
      // Silent fail for polling
    }
  };

  const formatTranscriptionTime = (isoStr) => {
    if (!isoStr) return "";
    try {
      const d = new Date(isoStr);
      return d.toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true });
    } catch { return isoStr; }
  };

  const formatTranscriptionDate = (isoStr) => {
    if (!isoStr) return "";
    try {
      const d = new Date(isoStr);
      return d.toLocaleDateString("en-US", { timeZone: "America/New_York", month: "numeric", day: "numeric" });
    } catch { return ""; }
  };

  const playTranscriptionAudio = async (transcriptionId) => {
    // Toggle off if same
    if (playingTranscription === transcriptionId) {
      if (transcriptionPlaying) {
        transcriptionAudioRef.current?.pause();
        setTranscriptionPlaying(false);
      } else {
        try { await transcriptionAudioRef.current?.play(); setTranscriptionPlaying(true); } catch {}
      }
      return;
    }
    // New audio
    setPlayingTranscription(transcriptionId);
    setTranscriptionPlaying(false);
    setTrAudioProgress(0);
    setTrAudioDuration(0);
    if (transcriptionBlobRef.current) {
      URL.revokeObjectURL(transcriptionBlobRef.current);
      transcriptionBlobRef.current = null;
    }
    try {
      const headers = {};
      const sessionToken = localStorage.getItem('session_token');
      if (sessionToken) headers.Authorization = `Bearer ${sessionToken}`;
      const res = await fetch(`${API}/radio/transcriptions/${transcriptionId}/audio`, { credentials: "include", headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      transcriptionBlobRef.current = url;
      if (transcriptionAudioRef.current) {
        transcriptionAudioRef.current.src = url;
        transcriptionAudioRef.current.load();
        await transcriptionAudioRef.current.play();
        setTranscriptionPlaying(true);
      }
    } catch (err) {
      console.error("Transcription audio error:", err);
      toast.error("Failed to play audio");
      setPlayingTranscription(null);
    }
  };

  const toggleTrSelect = (transcriptionId) => {
    setTrSelected(prev => {
      const next = new Set(prev);
      if (next.has(transcriptionId)) next.delete(transcriptionId);
      else next.add(transcriptionId);
      return next;
    });
  };

  const mergeSelectedTranscriptions = async () => {
    if (trSelected.size < 2) {
      toast.error("Select at least 2 transcriptions to merge");
      return;
    }
    setTrDownloading(true);
    try {
      const ids = Array.from(trSelected);
      const res = await axios.post(`${API}/radio/transcriptions/merge`, ids, {
        responseType: "blob",
        withCredentials: true,
      });
      const cd = res.headers["content-disposition"] || "";
      let name = `merged_${ids.length}.ogg`;
      const m = cd.match(/filename="?([^"]+)"?/);
      if (m) name = m[1];
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", name);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Merged ${ids.length} clips`);
      setTrSelected(new Set());
      setTrSelectMode(false);
    } catch (err) {
      console.error("Merge error:", err);
      toast.error(err?.response?.data?.detail || "Merge failed");
    } finally {
      setTrDownloading(false);
    }
  };

  const fetchChannels = async () => {
    try {
      const response = await axios.get(`${API}/radio/channels`);
      setChannels(response.data.channels || []);
    } catch (error) {
      console.error("Error fetching channels:", error);
    }
  };

  // Build the shared filter params for the recordings endpoint.
  const buildRecordingParams = () => {
    const params = new URLSearchParams();
    if (filterUser) params.append("user_id", filterUser);
    if (filterChannel) params.append("channel", filterChannel);
    if (filterDateFrom) params.append("date_from", filterDateFrom);
    if (filterDateTo) params.append("date_to", filterDateTo);
    if (filterTimeFrom) params.append("time_from", filterTimeFrom);
    if (filterTimeTo) params.append("time_to", filterTimeTo);
    if (searchQuery) params.append("search", searchQuery);
    return params;
  };

  const fetchRecordings = async (isFilterChange = false) => {
    try {
      setVisibleCount(100);
      if (isFilterChange) {
        setFiltering(true);
      } else {
        setLoading(true);
      }
      const params = buildRecordingParams();
      params.append("limit", String(RECORDINGS_PAGE));
      params.append("skip", "0");

      const response = await axios.get(`${API}/radio/recordings?${params.toString()}`, { withCredentials: true });
      setRecordings(response.data.recordings || []);
      setRecordingsTotal(response.data.total || (response.data.recordings || []).length);
    } catch (error) {
      console.error("Error fetching recordings:", error);
      toast.error("Failed to fetch recordings");
    } finally {
      setLoading(false);
      setFiltering(false);
    }
  };

  // Fetch the next server page and append. Used by "Load More".
  const loadMoreRecordings = async () => {
    if (loadingMoreRecordings) return;
    setLoadingMoreRecordings(true);
    try {
      const params = buildRecordingParams();
      params.append("limit", String(RECORDINGS_PAGE));
      params.append("skip", String(recordings.length));
      const response = await axios.get(`${API}/radio/recordings?${params.toString()}`, { withCredentials: true });
      const more = response.data.recordings || [];
      if (more.length > 0) {
        // Dedup by filename in case a new recording shifted the offset.
        setRecordings(prev => {
          const seen = new Set(prev.map(r => r.filename));
          return [...prev, ...more.filter(r => !seen.has(r.filename))];
        });
      }
      if (typeof response.data.total === "number") setRecordingsTotal(response.data.total);
      setVisibleCount(prev => prev + 100);
    } catch (error) {
      console.error("Error loading more recordings:", error);
      toast.error("Failed to load more");
    } finally {
      setLoadingMoreRecordings(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/radio/users`);
      setUsers(response.data.users || []);
      setAppUsers(response.data.app_users || []);
      setRadioUsers(response.data.radio_users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const syncRecordings = async () => {
    try {
      setSyncing(true);
      const response = await axios.get(`${API}/radio/recordings/sync?force=true`, { withCredentials: true });
      toast.success(`Synced! ${response.data.new_recordings} new recordings`);
      fetchRecordings();
      fetchUsers();
      fetchChannels();
    } catch (error) {
      console.error("Error syncing:", error);
      toast.error("Failed to sync recordings");
    } finally {
      setSyncing(false);
    }
  };

  // Store blob URLs to revoke when done
  const audioBlobUrlRef = useRef(null);
  const recordingRefs = useRef({});

  // Auto-scroll to playing recording
  const scrollToRecording = (filename) => {
    const element = recordingRefs.current[filename];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const playRecording = async (filename) => {
    // If same file is playing, toggle pause/play
    if (playingFile === filename) {
      if (isPlaying) {
        audioRef.current?.pause();
        setIsPlaying(false);
      } else {
        // Resume from current position
        try {
          await audioRef.current?.play();
          setIsPlaying(true);
        } catch (err) {
          console.error("Resume error:", err);
        }
      }
      return;
    }

    // Different file - load and play
    setPlayingFile(filename);
    setAudioProgress(0);
    if (audioRef.current) {
      try {
        // Reset audio element
        audioRef.current.pause();
        audioRef.current.currentTime = 0;

        // Revoke previous blob URL if exists
        if (audioBlobUrlRef.current) {
          URL.revokeObjectURL(audioBlobUrlRef.current);
          audioBlobUrlRef.current = null;
        }

        // Fetch audio with axios (includes session token header for iOS auth).
        // withCredentials lets the cookie session carry the request through
        // when localStorage's token is briefly empty.
        const response = await axios.get(`${API}/radio/stream/${filename}`, {
          responseType: 'blob',
          withCredentials: true,
        });

        const blob = response.data;
        const blobUrl = URL.createObjectURL(blob);
        audioBlobUrlRef.current = blobUrl;

        audioRef.current.src = blobUrl;
        audioRef.current.load();

        // Wait for audio to be ready before playing
        await new Promise((resolve, reject) => {
          const onCanPlay = () => {
            audioRef.current.removeEventListener('canplay', onCanPlay);
            audioRef.current.removeEventListener('error', onError);
            resolve();
          };
          const onError = (e) => {
            audioRef.current.removeEventListener('canplay', onCanPlay);
            audioRef.current.removeEventListener('error', onError);
            reject(e);
          };
          audioRef.current.addEventListener('canplay', onCanPlay, { once: true });
          audioRef.current.addEventListener('error', onError, { once: true });
        });

        await audioRef.current.play();
        setIsPlaying(true);

        // Auto-scroll to the playing recording
        scrollToRecording(filename);
      } catch (err) {
        console.error("Playback error:", err);
        const status = err?.response?.status;
        if (status === 502) {
          toast.error("Recording temporarily unavailable — try again in a moment");
        } else {
          toast.error("Failed to play audio");
        }
        setIsPlaying(false);
      }
    }
  };

  // Seek to position in audio
  const seekAudio = (e, progressBarWidth) => {
    if (!audioRef.current || !audioDuration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / progressBarWidth;
    const newTime = percentage * audioDuration;
    audioRef.current.currentTime = newTime;
    setAudioProgress(newTime);
  };

  // Auto-advance to next (newer) recording when current one ends
  const playNextRecording = () => {
    if (!playingFile || filteredRecordings.length === 0) {
      setIsPlaying(false);
      setAudioProgress(0);
      return;
    }

    const currentIndex = filteredRecordings.findIndex(r => r.filename === playingFile);
    if (currentIndex === -1 || currentIndex <= 0) {
      // No newer recording (at top of list), stop
      setIsPlaying(false);
      setAudioProgress(0);
      setPlayingFile(null);
      return;
    }

    // Play the next (newer) recording - go up the list
    const nextRecording = filteredRecordings[currentIndex - 1];
    playRecording(nextRecording.filename);
  };

  const transcribeRecording = async (filename) => {
    setTranscribingFile(filename);
    try {
      const decodedFilename = decodeURIComponent(filename);
      const response = await axios.post(`${API}/radio/recordings/transcribe`, null, { params: { filename: decodedFilename }, withCredentials: true });
      // Update the recording in state with transcription
      setRecordings(prev => prev.map(r => r.filename === filename ? { ...r, transcription: response.data.transcription, transcription_status: "completed" } : r));
      toast.success("Transcription complete");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Transcription failed");
    } finally {
      setTranscribingFile(null);
    }
  };

  const transcribeAllNew = async () => {
    setTranscribingAll(true);
    try {
      const response = await axios.post(`${API}/radio/recordings/transcribe-new`, {}, { withCredentials: true });
      toast.success(`Transcribed ${response.data.transcribed} recordings${response.data.failed ? `, ${response.data.failed} failed` : ""}`);
      // Refresh recordings to get updated transcriptions
      fetchRecordings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Batch transcription failed");
    } finally {
      setTranscribingAll(false);
    }
  };

  const downloadRecording = async (filename) => {
    try {
      toast.info("Downloading...");
      const response = await axios.get(`${API}/radio/download/${filename}`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Downloaded!");
    } catch (error) {
      console.error("Error downloading:", error);
      toast.error("Failed to download");
    }
  };

  const toggleSelect = (filename) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(filename)) {
      newSelected.delete(filename);
    } else {
      newSelected.add(filename);
    }
    setSelectedFiles(newSelected);
  };

  const selectAll = () => {
    if (selectedFiles.size === filteredRecordings.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(filteredRecordings.map(r => r.filename)));
    }
  };

  const mergeSelected = async () => {
    if (selectedFiles.size < 2) {
      toast.error("Select at least 2 files to merge");
      return;
    }

    try {
      setMerging(true);
      const filenames = Array.from(selectedFiles).sort();
      setMergeProgress({ state: "starting", downloaded: 0, total: filenames.length });

      // Kick off the merge job — backend now returns a job_id immediately
      // and runs the download/concat in the background.
      const startResp = await axios.post(`${API}/radio/merge`, filenames);
      const jobId = startResp.data?.job_id;
      if (!jobId) throw new Error("Server did not return a job id");
      setMergeProgress({ state: "downloading", downloaded: 0, total: filenames.length });

      // Poll until done. 500ms feels live without flooding the backend.
      let status;
      while (true) {
        await new Promise((r) => setTimeout(r, 500));
        const s = await axios.get(`${API}/radio/merge/status/${jobId}`);
        status = s.data;
        setMergeProgress({
          state: status.state,
          downloaded: status.downloaded || 0,
          total: status.total || filenames.length,
        });
        if (status.state === "done") break;
        if (status.state === "error") {
          throw new Error(status.error || "Merge failed");
        }
      }

      // Pull the finished file as a blob and trigger the browser download.
      const fileResp = await axios.get(`${API}/radio/merge/download/${jobId}`, {
        responseType: "blob",
      });
      const contentDisposition = fileResp.headers["content-disposition"];
      let downloadFilename = status.filename || "merged.wav";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) downloadFilename = match[1];
      }
      const url = window.URL.createObjectURL(new Blob([fileResp.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Saved ${downloadFilename} to your Downloads`, { duration: 6000 });
      setSelectedFiles(new Set());
      setSelectMode(false);
    } catch (error) {
      // The start POST returns JSON, but the download endpoint may return a
      // Blob — handle both shapes so we surface the actual reason.
      let detail = error?.message || "Failed to merge";
      try {
        const respData = error?.response?.data;
        if (respData && typeof respData.text === "function") {
          const body = await respData.text();
          try {
            const parsed = JSON.parse(body);
            detail = parsed?.detail || body || detail;
          } catch {
            detail = body || detail;
          }
        } else if (respData?.detail) {
          detail = respData.detail;
        }
      } catch {}
      console.error("[merge] failed:", detail, error);
      toast.error(detail);
    } finally {
      setMerging(false);
      setMergeProgress(null);
    }
  };

  const deleteSelected = async () => {
    if (selectedFiles.size === 0) {
      toast.error("Select at least 1 file to delete");
      return;
    }

    if (!window.confirm(`Delete ${selectedFiles.size} recording(s)? This cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(true);
      const filenames = Array.from(selectedFiles);
      await axios.delete(`${API}/radio/recordings`, {
        data: filenames
      });

      toast.success(`Deleted ${selectedFiles.size} recordings`);
      setSelectedFiles(new Set());
      setSelectMode(false);
      fetchRecordings();
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error("Failed to delete recordings");
    } finally {
      setDeleting(false);
    }
  };

  const clearFilters = () => {
    setFilterUser("");
    setFilterUserType("");
    setFilterChannel("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterTimeFrom("");
    setFilterTimeTo("");
    setSearchQuery("");
  };

  const filteredRecordings = useMemo(() => {
    let result = recordings;

    // Filter by user type (app users vs radio-only users)
    if (filterUserType === "app" && appUsers.length > 0) {
      const appUserSet = new Set(appUsers);
      result = result.filter(r => appUserSet.has(r.user_id));
    } else if (filterUserType === "radio" && radioUsers.length > 0) {
      const radioUserSet = new Set(radioUsers);
      result = result.filter(r => radioUserSet.has(r.user_id));
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(r =>
        r.filename.toLowerCase().includes(query) ||
        r.user_id.toLowerCase().includes(query) ||
        (r.transcription && r.transcription.toLowerCase().includes(query))
      );
    }
    return result;
  }, [recordings, searchQuery, filterUserType, appUsers, radioUsers]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRecordings(true); // isFilterChange = true, no flash
    }, 300);
    return () => clearTimeout(timer);
  }, [filterUser, filterChannel, filterDateFrom, filterDateTo, filterTimeFrom, filterTimeTo]);

  const hasActiveFilters = filterUser || filterUserType || filterChannel || filterDateFrom || filterDateTo || filterTimeFrom || filterTimeTo;
  const activeFilterCount = [filterUser, filterUserType, filterChannel, filterDateFrom, filterDateTo, filterTimeFrom, filterTimeTo].filter(Boolean).length;

  // Get filtered user list based on user type selection
  const filteredUserList = useMemo(() => {
    if (filterUserType === "app") return appUsers;
    if (filterUserType === "radio") return radioUsers;
    return users;
  }, [filterUserType, users, appUsers, radioUsers]);

  return (
    <div className="h-[calc(100dvh-8rem)] flex flex-col gap-2">
      {/* Hidden audio element - iOS compatible */}
      <audio
        ref={audioRef}
        playsInline
        preload="metadata"
        onEnded={playNextRecording}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        onTimeUpdate={(e) => setAudioProgress(e.target.currentTime)}
        onLoadedMetadata={(e) => setAudioDuration(e.target.duration)}
        onError={(e) => {
          // MediaError prints as "[object MediaError]" without unpacking;
          // surface the .code (1=abort 2=net 3=decode 4=unsupported) and
          // .message so the cause is actually visible in console.
          const err = e.target.error;
          const codeNames = { 1: "ABORTED", 2: "NETWORK", 3: "DECODE", 4: "UNSUPPORTED" };
          const codeLabel = err ? `${err.code} (${codeNames[err.code] || "?"})` : "(no error obj)";
          console.error("Audio error:", { code: codeLabel, message: err?.message || "", src: e.target.currentSrc });
          if (err?.code === 3) {
            toast.error("Recording bytes can't be decoded — likely a corrupt upload or server hiccup");
          } else if (err?.code === 4) {
            toast.error("Audio format not supported by this browser");
          } else {
            toast.error("Failed to load audio");
          }
          setIsPlaying(false);
        }}
      />

      {/* Hidden audio element for transcription playback */}
      <audio
        ref={transcriptionAudioRef}
        playsInline
        preload="none"
        onEnded={() => { setTranscriptionPlaying(false); setPlayingTranscription(null); setTrAudioProgress(0); }}
        onPause={() => setTranscriptionPlaying(false)}
        onPlay={() => setTranscriptionPlaying(true)}
        onTimeUpdate={(e) => setTrAudioProgress(e.target.currentTime)}
        onLoadedMetadata={(e) => setTrAudioDuration(e.target.duration)}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <TabsList className="w-fit bg-slate-900 flex-shrink-0">
          {has71Access && (
            <TabsTrigger value="71" className="flex items-center gap-1.5 text-sm">
              <Radio className="w-4 h-4" />
              71
            </TabsTrigger>
          )}
          {hasRecordingsAccess && (
            <TabsTrigger value="recordings" className="flex items-center gap-1.5 text-sm">
              <Headphones className="w-4 h-4" />
              Recordings
            </TabsTrigger>
          )}
        </TabsList>

        {/* ============== 71 Transcription Tab ============== */}
        <TabsContent value="71" className="flex flex-col flex-1 min-h-0 mt-0 gap-2 data-[state=inactive]:hidden overflow-hidden">
          {/* Search / filter bar */}
          <Card className="bg-slate-900 border-slate-800 flex-shrink-0">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
                  <Input
                    placeholder="Search transcriptions..."
                    value={transcriptionSearch}
                    onChange={(e) => setTranscriptionSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && fetchTranscriptions()}
                    className="input-dark pl-8 h-8 text-sm"
                  />
                </div>
                <Button size="sm" className="h-8" onClick={() => fetchTranscriptions()}>
                  <Search className="w-3 h-3 mr-1" /> Search
                </Button>
                {transcriptionSearch && (
                  <Button size="sm" variant="ghost" className="h-8" onClick={() => { setTranscriptionSearch(""); fetchTranscriptions(""); }}>
                    <X className="w-3 h-3" />
                  </Button>
                )}
                <Button size="sm" variant="outline" className="h-8" onClick={() => fetchTranscriptions()}>
                  <RefreshCw className="w-3 h-3" />
                </Button>
                <Button
                  size="sm"
                  variant={trSelectMode ? "default" : "outline"}
                  className="h-8"
                  onClick={() => {
                    setTrSelectMode(!trSelectMode);
                    if (trSelectMode) setTrSelected(new Set());
                  }}
                >
                  {trSelectMode ? <CheckSquare className="w-3 h-3" /> : <Square className="w-3 h-3" />}
                </Button>
              </div>
              {trSelectMode && (
                <div className="flex items-center justify-between flex-wrap gap-2 mt-2 pt-2 border-t border-slate-800">
                  <span className="text-xs text-slate-300">
                    {trSelected.size > 0 ? `${trSelected.size} selected` : "Tap to select"}
                  </span>
                  {trSelected.size >= 2 && (
                    <Button
                      size="sm"
                      onClick={mergeSelectedTranscriptions}
                      disabled={trDownloading}
                      className="bg-green-600 hover:bg-green-700 h-8 px-3"
                    >
                      {trDownloading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Merge className="w-4 h-4 mr-1" />}
                      Merge ({trSelected.size})
                    </Button>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="datetime-local"
                  value={trDateFrom}
                  onChange={(e) => setTrDateFrom(e.target.value)}
                  className="input-dark h-8 text-xs flex-1"
                  placeholder="From"
                />
                <span className="text-slate-500 text-xs">to</span>
                <Input
                  type="datetime-local"
                  value={trDateTo}
                  onChange={(e) => setTrDateTo(e.target.value)}
                  className="input-dark h-8 text-xs flex-1"
                />
                {(trDateFrom || trDateTo) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs text-slate-400 px-2"
                    onClick={() => { setTrDateFrom(""); setTrDateTo(""); fetchTranscriptions(); }}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Transcription feed */}
          <Card className="bg-slate-900 border-slate-800 flex-1 min-h-0 flex flex-col">
            <CardHeader className="py-2 px-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm uppercase tracking-wider flex items-center gap-2">
                  <Radio className="w-4 h-4 text-green-400" />
                  Live Feed
                  {transcriptionTotal > 0 && (
                    <Badge variant="outline" className="text-[10px]">{transcriptionTotal}</Badge>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`h-6 px-2 text-[10px] ${showTranscriptText ? 'text-slate-400' : 'text-amber-400'}`}
                    onClick={() => setShowTranscriptText(!showTranscriptText)}
                    title={showTranscriptText ? "Hide transcripts" : "Show transcripts"}
                  >
                    {showTranscriptText ? <Eye className="w-3 h-3 mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
                    {showTranscriptText ? "Text" : "Text"}
                  </Button>
                  {!transcriptionSearch && transcriptions.length > 0 && (
                    <div className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-[10px] text-green-400">Live</span>
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0 flex-1 min-h-0">
              {loadingTranscriptions ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : transcriptions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-500 gap-2">
                  <Radio className="w-8 h-8" />
                  <span className="text-sm">No transcriptions yet</span>
                  <span className="text-xs">Waiting for radio transmissions...</span>
                </div>
              ) : (
                <ScrollArea className="h-full" ref={transcriptionFeedRef}>
                  <div className="divide-y divide-slate-800">
                    {transcriptions.length > 0 && transcriptions.length < transcriptionTotal && (
                      <div className="px-4 py-2 text-center text-xs text-slate-500">
                        Showing {transcriptions.length} of {transcriptionTotal}
                      </div>
                    )}
                    {transcriptions.map((t) => {
                      const isThisPlaying = playingTranscription === t.transcription_id;
                      const isActive = isThisPlaying && transcriptionPlaying;
                      const isPaused = isThisPlaying && !transcriptionPlaying;
                      const isTrSelected = trSelected.has(t.transcription_id);
                      return (
                      <div
                        key={t.transcription_id}
                        className={`px-4 py-3 hover:bg-slate-800/50 transition-colors ${isActive ? "bg-green-900/20" : ""} ${isTrSelected ? "bg-blue-900/30" : ""} ${trSelectMode ? "cursor-pointer" : ""}`}
                        onClick={() => { if (trSelectMode) toggleTrSelect(t.transcription_id); }}
                      >
                        <div className="flex items-start gap-3">
                          {trSelectMode && (
                            <Checkbox
                              checked={isTrSelected}
                              onCheckedChange={() => toggleTrSelect(t.transcription_id)}
                              className="flex-shrink-0 mt-1"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                          <div className="flex-shrink-0 mt-0.5">
                            <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center">
                              <Radio className="w-4 h-4 text-green-400" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="text-xs font-medium text-slate-400">
                                {formatTranscriptionDate(t.created_at)} {formatTranscriptionTime(t.created_at)}
                              </span>
                              {t.channel && (
                                <Badge className="text-[9px] px-1.5 py-0 bg-blue-600/30 text-blue-400">{t.channel}</Badge>
                              )}
                              {t.duration_seconds && (
                                <span className="text-[10px] text-slate-500">{Math.round(t.duration_seconds)}s</span>
                              )}
                            </div>
                            {showTranscriptText && <p className="text-sm text-slate-200 leading-relaxed">{t.text}</p>}
                            {/* Audio player bar */}
                            {t.audio_r2_key && (
                              <div className="flex items-center gap-2 mt-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className={`h-7 w-7 p-0 shrink-0 ${isActive ? "text-green-400" : isPaused ? "text-amber-400" : "text-slate-400"}`}
                                  onClick={() => playTranscriptionAudio(t.transcription_id)}
                                >
                                  {isActive ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                                </Button>
                                <input
                                  type="range"
                                  min="0"
                                  max="1000"
                                  value={isThisPlaying && trAudioDuration > 0 ? Math.round((trAudioProgress / trAudioDuration) * 1000) : 0}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value) / 1000;
                                    if (transcriptionAudioRef.current && trAudioDuration > 0) {
                                      transcriptionAudioRef.current.currentTime = val * trAudioDuration;
                                      setTrAudioProgress(val * trAudioDuration);
                                    }
                                  }}
                                  className="flex-1 h-1 accent-green-500 cursor-pointer"
                                />
                                <span className="text-[10px] text-slate-500 shrink-0 w-10 text-right font-mono">
                                  {isThisPlaying && trAudioDuration > 0
                                    ? `${Math.floor(trAudioProgress / 60)}:${String(Math.floor(trAudioProgress % 60)).padStart(2, '0')}`
                                    : t.duration_seconds
                                      ? `${Math.floor(t.duration_seconds / 60)}:${String(Math.floor(t.duration_seconds % 60)).padStart(2, '0')}`
                                      : "--:--"
                                  }
                                </span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-slate-400 hover:text-blue-400 shrink-0"
                                  onClick={async () => {
                                    try {
                                      // If we already have the blob from playback, reuse it
                                      if (transcriptionBlobRef.current && isThisPlaying) {
                                        const resp = await fetch(transcriptionBlobRef.current);
                                        const blob = await resp.blob();
                                        const a = document.createElement("a");
                                        a.href = URL.createObjectURL(blob);
                                        a.download = `${t.transcription_id}.ogg`;
                                        a.click();
                                        URL.revokeObjectURL(a.href);
                                      } else {
                                        const headers = {};
                                        const sessionToken = localStorage.getItem('session_token');
                                        if (sessionToken) headers.Authorization = `Bearer ${sessionToken}`;
                                        const resp = await fetch(`${API}/radio/transcriptions/${t.transcription_id}/audio`, { credentials: "include", headers });
                                        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                                        const blob = await resp.blob();
                                        const a = document.createElement("a");
                                        a.href = URL.createObjectURL(blob);
                                        a.download = `${t.transcription_id}.ogg`;
                                        a.click();
                                        URL.revokeObjectURL(a.href);
                                      }
                                      toast.success("Downloaded!");
                                    } catch (err) {
                                      console.error("Download error:", err);
                                      toast.error("Failed to download");
                                    }
                                  }}
                                  title="Download audio"
                                >
                                  <Download className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      );
                    })}
                    {transcriptions.length < transcriptionTotal && (
                      <div className="px-4 py-3 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs"
                          disabled={loadingMore}
                          onClick={() => fetchTranscriptions(undefined, true)}
                        >
                          {loadingMore ? (
                            <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Loading...</>
                          ) : (
                            `Load More (${transcriptionTotal - transcriptions.length} remaining)`
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============== Recordings Tab ============== */}
        <TabsContent value="recordings" className="flex flex-col flex-1 min-h-0 mt-0 gap-4 data-[state=inactive]:hidden overflow-hidden">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : (<>
      {/* Header Card */}
      <Card className="bg-slate-900 border-slate-800 flex-shrink-0">
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3">
            {/* Title Row */}
            <div className="flex items-center gap-2 min-w-0">
              <Headphones className="w-5 h-5 text-blue-500 flex-shrink-0" />
              <CardTitle className="text-lg sm:text-xl uppercase tracking-wider">
                Radio
              </CardTitle>
              {filtering && <Loader2 className="w-4 h-4 animate-spin text-blue-400 flex-shrink-0" />}
              <Badge variant="outline" className="flex-shrink-0">
                {recordingsTotal > filteredRecordings.length ? `${filteredRecordings.length} / ${recordingsTotal}` : filteredRecordings.length}
              </Badge>
            </div>

            {/* Search Row */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search recordings..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-slate-800 border-slate-700"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <Button
                variant={showFilters || hasActiveFilters ? "default" : "outline"}
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="relative"
              >
                <Filter className="w-4 h-4" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <div className="pt-3 border-t border-slate-700">
                {/* Action toolbar */}
                <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b border-slate-800">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`h-8 px-2 text-xs ${viewMode === "compact" ? 'text-blue-400' : 'text-slate-400'}`}
                    onClick={() => setViewMode(viewMode === "compact" ? "detailed" : "compact")}
                    title={viewMode === "compact" ? "Switch to detailed view" : "Switch to compact audio-only view"}
                  >
                    {viewMode === "compact" ? <LayoutList className="w-3.5 h-3.5 mr-1" /> : <List className="w-3.5 h-3.5 mr-1" />}
                    View
                  </Button>
                  {viewMode !== "compact" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-8 px-2 text-xs ${showRecordingText ? 'text-slate-400' : 'text-amber-400'}`}
                      onClick={() => setShowRecordingText(!showRecordingText)}
                      title={showRecordingText ? "Hide transcripts" : "Show transcripts"}
                    >
                      {showRecordingText ? <Eye className="w-3.5 h-3.5 mr-1" /> : <EyeOff className="w-3.5 h-3.5 mr-1" />}
                      Text
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={syncRecordings}
                    disabled={syncing}
                    className="h-8 px-3 text-xs"
                  >
                    {syncing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                    Sync
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={transcribeAllNew}
                    disabled={transcribingAll}
                    className="h-8 px-3 text-xs"
                  >
                    {transcribingAll ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5 mr-1" />}
                    STT
                  </Button>
                  <Button
                    variant={selectMode ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setSelectMode(!selectMode);
                      if (selectMode) setSelectedFiles(new Set());
                    }}
                    className="h-8 px-3 text-xs"
                  >
                    {selectMode ? <CheckSquare className="w-3.5 h-3.5 mr-1" /> : <Square className="w-3.5 h-3.5 mr-1" />}
                    {selectMode ? "Done" : "Select"}
                  </Button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 overflow-hidden">
                  <div className="min-w-0">
                    <label className="text-xs text-slate-400 mb-1 block">Channel</label>
                    <Select value={filterChannel || "all"} onValueChange={(v) => setFilterChannel(v === "all" ? "" : v)}>
                      <SelectTrigger className="h-8 bg-slate-800 border-slate-700 text-sm">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Channels</SelectItem>
                        {channels.map(ch => (
                          <SelectItem key={ch.id} value={ch.id}>{ch.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="min-w-0">
                    <label className="text-xs text-slate-400 mb-1 block">User Type</label>
                    <Select value={filterUserType || "all"} onValueChange={(v) => {
                      setFilterUserType(v === "all" ? "" : v);
                      setFilterUser(""); // Clear specific user when changing type
                    }}>
                      <SelectTrigger className="h-8 bg-slate-800 border-slate-700 text-sm">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="app">App Users ({appUsers.length})</SelectItem>
                        <SelectItem value="radio">Radio Only ({radioUsers.length})</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="min-w-0">
                    <label className="text-xs text-slate-400 mb-1 block">User</label>
                    <Select value={filterUser || "all"} onValueChange={(v) => setFilterUser(v === "all" ? "" : v)}>
                      <SelectTrigger className="h-8 bg-slate-800 border-slate-700 text-sm">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60 overflow-y-auto">
                        <SelectItem value="all">All Users ({filteredUserList.length})</SelectItem>
                        {filteredUserList.map(u => (
                          <SelectItem key={u} value={u}>{u}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="min-w-0">
                    <label className="text-xs text-slate-400 mb-1 block">From</label>
                    <Input
                      type="date"
                      value={filterDateFrom}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                      className="h-8 bg-slate-800 border-slate-700 text-sm w-full"
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="text-xs text-slate-400 mb-1 block">To</label>
                    <Input
                      type="date"
                      value={filterDateTo}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                      className="h-8 bg-slate-800 border-slate-700 text-sm w-full"
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="text-xs text-slate-400 mb-1 block">After</label>
                    <Input
                      type="time"
                      value={filterTimeFrom}
                      onChange={(e) => setFilterTimeFrom(e.target.value)}
                      className="h-8 bg-slate-800 border-slate-700 text-sm w-full"
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="text-xs text-slate-400 mb-1 block">Before</label>
                    <Input
                      type="time"
                      value={filterTimeTo}
                      onChange={(e) => setFilterTimeTo(e.target.value)}
                      className="h-8 bg-slate-800 border-slate-700 text-sm w-full"
                    />
                  </div>
                </div>
                {hasActiveFilters && (
                  <div className="mt-2 flex justify-end">
                    <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 text-xs">
                      <X className="h-3 w-3 mr-1" /> Clear
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Select Mode Bar */}
            {selectMode && (
              <div className="pt-3 border-t border-slate-700 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={selectedFiles.size === filteredRecordings.length && filteredRecordings.length > 0}
                    onCheckedChange={selectAll}
                  />
                  <span className="text-sm text-slate-300">
                    {selectedFiles.size > 0 ? `${selectedFiles.size} selected` : "Select all"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {selectedFiles.size >= 2 && (
                    <Button
                      size="sm"
                      onClick={mergeSelected}
                      disabled={merging}
                      className="bg-green-600 hover:bg-green-700 h-8 px-3"
                    >
                      {merging ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Merge className="w-4 h-4 mr-1" />}
                      Merge ({selectedFiles.size})
                    </Button>
                  )}
                  {selectedFiles.size >= 1 && (
                    <Button
                      size="sm"
                      onClick={deleteSelected}
                      disabled={deleting}
                      className="bg-red-600 hover:bg-red-700 h-8 px-3"
                    >
                      {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Recordings List */}
      <Card className="bg-slate-900 border-slate-800 flex-1 overflow-hidden">
        <CardContent className="p-0 h-full">
          {filteredRecordings.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-12 text-slate-400">
              <FileAudio className="w-16 h-16 mb-4 opacity-50" />
              <p className="text-lg font-medium">No recordings</p>
              <p className="text-sm mt-1">Sync to fetch recordings</p>
              <Button variant="outline" className="mt-4" onClick={syncRecordings} disabled={syncing}>
                {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Sync Now
              </Button>
            </div>
          ) : (
            <ScrollArea className="h-full">
              <div className="divide-y divide-slate-800">
                {filteredRecordings.slice(0, visibleCount).map((rec, idx, arr) => {
                  const isThisFile = playingFile === rec.filename;
                  const isCurrentlyPlaying = isThisFile && isPlaying;
                  const isPaused = isThisFile && !isPlaying;
                  const isSelected = selectedFiles.has(rec.filename);
                  const prevRec = idx > 0 ? arr[idx - 1] : null;
                  const showDateHeader = viewMode === "compact" && (!prevRec || prevRec.date !== rec.date);

                  if (viewMode === "compact") {
                    const initial = (rec.user_id || "?").charAt(0).toUpperCase();
                    return (
                      <Fragment key={rec.filename}>
                        {showDateHeader && (
                          <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur px-3 py-1.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-300 border-b border-slate-700">
                            {formatDateLong(rec.date)}
                          </div>
                        )}
                      <div
                        ref={(el) => { recordingRefs.current[rec.filename] = el; }}
                        className={`flex flex-col gap-1 px-3 py-2 hover:bg-slate-800/50 transition-colors cursor-pointer ${
                          isSelected ? "bg-blue-900/30" : ""
                        } ${isCurrentlyPlaying ? "bg-green-900/20" : ""} ${isPaused ? "bg-amber-900/10" : ""}`}
                        onClick={() => {
                          if (selectMode) toggleSelect(rec.filename);
                          else setTranscriptDialogFile(rec.filename);
                        }}
                      >
                        <div className="flex items-center gap-3">
                          {selectMode && (
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleSelect(rec.filename)}
                              className="flex-shrink-0"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); playRecording(rec.filename); }}
                            className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-colors group ${
                              isCurrentlyPlaying ? "bg-green-500 text-white" : isPaused ? "bg-amber-500 text-white" : "bg-slate-700 text-slate-200 hover:bg-blue-600 hover:text-white"
                            }`}
                            aria-label={isCurrentlyPlaying ? "Pause" : "Play"}
                          >
                            {isCurrentlyPlaying ? (
                              <Pause className="w-3.5 h-3.5" />
                            ) : isPaused ? (
                              <Play className="w-3.5 h-3.5 ml-0.5" />
                            ) : (
                              <>
                                <span className="group-hover:hidden">{initial}</span>
                                <Play className="w-3.5 h-3.5 ml-0.5 hidden group-hover:block" />
                              </>
                            )}
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-slate-100 truncate leading-tight">{rec.user_id}</div>
                            <div className="flex items-center gap-1 text-xs text-slate-400 leading-tight">
                              <span className="inline-block">↗</span>
                              <span>{formatTime(rec.time)}</span>
                              {rec.channel_name && (
                                <span className="text-slate-500 truncate">· {rec.channel_name}</span>
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-slate-400 tabular-nums flex-shrink-0">
                            {formatFileSize(rec.size)}
                          </div>
                        </div>
                        {isThisFile && audioDuration > 0 && (
                          <div className="flex items-center gap-2 pl-[48px]" onClick={(e) => e.stopPropagation()}>
                            <span className="text-[10px] text-slate-400 w-8 text-right tabular-nums">
                              {Math.floor(audioProgress / 60)}:{String(Math.floor(audioProgress % 60)).padStart(2, '0')}
                            </span>
                            <div
                              className="flex-1 h-1.5 bg-slate-700 rounded-full cursor-pointer relative group"
                              onClick={(e) => { e.stopPropagation(); seekAudio(e, e.currentTarget.offsetWidth); }}
                            >
                              <div
                                className={`h-full rounded-full transition-all ${isCurrentlyPlaying ? 'bg-green-500' : 'bg-amber-500'}`}
                                style={{ width: `${(audioProgress / audioDuration) * 100}%` }}
                              />
                            </div>
                            <span className="text-[10px] text-slate-400 w-8 tabular-nums">
                              {Math.floor(audioDuration / 60)}:{String(Math.floor(audioDuration % 60)).padStart(2, '0')}
                            </span>
                          </div>
                        )}
                      </div>
                      </Fragment>
                    );
                  }

                  return (
                    <div
                      key={rec.filename}
                      ref={(el) => { recordingRefs.current[rec.filename] = el; }}
                      className={`flex flex-col gap-2 px-4 py-3 hover:bg-slate-800/50 transition-colors ${
                        isSelected ? "bg-blue-900/30" : ""
                      } ${isCurrentlyPlaying ? "bg-green-900/20" : ""} ${isPaused ? "bg-amber-900/10" : ""}`}
                      onClick={() => selectMode && toggleSelect(rec.filename)}
                    >
                      <div className="flex items-center gap-3">
                        {/* Checkbox */}
                        {selectMode && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelect(rec.filename)}
                            className="flex-shrink-0"
                          />
                        )}

                        {/* Play Button */}
                        <button
                          onClick={(e) => { e.stopPropagation(); playRecording(rec.filename); }}
                          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                            isCurrentlyPlaying
                              ? "bg-green-500 text-white"
                              : isPaused
                                ? "bg-amber-500 text-white"
                                : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                          }`}
                        >
                          {isCurrentlyPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                        </button>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {rec.channel_name && (
                              <Badge variant="outline" className="text-xs bg-blue-900/50 border-blue-700 text-blue-300">
                                {rec.channel_name}
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-xs bg-slate-800 border-slate-700">
                              {rec.user_id}
                            </Badge>
                            <span className="text-sm font-medium text-slate-200">
                              {formatDate(rec.date)}
                            </span>
                            <span className="text-sm text-slate-400">
                              {formatTime(rec.time)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-slate-500">
                              {formatFileSize(rec.size)}
                            </span>
                          </div>
                        </div>

                        {/* Playing/Paused Indicator */}
                        {isCurrentlyPlaying && (
                          <div className="flex items-center gap-0.5">
                            <div className="w-0.5 h-3 bg-green-500 rounded animate-pulse"></div>
                            <div className="w-0.5 h-4 bg-green-500 rounded animate-pulse" style={{ animationDelay: '150ms' }}></div>
                            <div className="w-0.5 h-2 bg-green-500 rounded animate-pulse" style={{ animationDelay: '300ms' }}></div>
                          </div>
                        )}
                        {isPaused && (
                          <span className="text-xs text-amber-400 uppercase tracking-wider">Paused</span>
                        )}

                        {/* Download Button */}
                        {!selectMode && (
                          <button
                            onClick={(e) => { e.stopPropagation(); downloadRecording(rec.filename); }}
                            className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      {/* Transcription */}
                      {rec.transcription && showRecordingText && (
                        <div className="pl-[52px] pr-2 pb-1">
                          <p className="text-xs text-slate-400 italic leading-relaxed">
                            <MessageSquare className="w-3 h-3 inline mr-1 text-slate-500" />
                            {rec.transcription}
                          </p>
                        </div>
                      )}
                      {!rec.transcription && !selectMode && (
                        <div className="pl-[52px] pr-2 pb-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); transcribeRecording(rec.filename); }}
                            disabled={transcribingFile === rec.filename}
                            className="text-[10px] text-slate-500 hover:text-slate-300 flex items-center gap-1"
                          >
                            {transcribingFile === rec.filename ? (
                              <><Loader2 className="w-3 h-3 animate-spin" /> Transcribing...</>
                            ) : (
                              <><MessageSquare className="w-3 h-3" /> Transcribe</>
                            )}
                          </button>
                        </div>
                      )}

                      {/* Seekbar - shown when this file is playing or paused */}
                      {isThisFile && audioDuration > 0 && (
                        <div className="flex items-center gap-2 pl-[52px]">
                          <span className="text-xs text-slate-400 w-10 text-right">
                            {Math.floor(audioProgress / 60)}:{String(Math.floor(audioProgress % 60)).padStart(2, '0')}
                          </span>
                          <div
                            className="flex-1 h-2 bg-slate-700 rounded-full cursor-pointer relative group"
                            onClick={(e) => { e.stopPropagation(); seekAudio(e, e.currentTarget.offsetWidth); }}
                          >
                            <div
                              className={`h-full rounded-full transition-all ${isCurrentlyPlaying ? 'bg-green-500' : 'bg-amber-500'}`}
                              style={{ width: `${(audioProgress / audioDuration) * 100}%` }}
                            />
                            <div
                              className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity ${isCurrentlyPlaying ? 'bg-green-400' : 'bg-amber-400'}`}
                              style={{ left: `calc(${(audioProgress / audioDuration) * 100}% - 6px)` }}
                            />
                          </div>
                          <span className="text-xs text-slate-400 w-10">
                            {Math.floor(audioDuration / 60)}:{String(Math.floor(audioDuration % 60)).padStart(2, '0')}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
                {(visibleCount < filteredRecordings.length || recordings.length < recordingsTotal) && (
                  <div className="p-3 text-center">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={loadingMoreRecordings}
                      onClick={() => {
                        // First exhaust what's already loaded client-side (instant),
                        // then page the next chunk from the server.
                        if (visibleCount < filteredRecordings.length) {
                          setVisibleCount(prev => prev + 100);
                        } else {
                          loadMoreRecordings();
                        }
                      }}
                    >
                      {loadingMoreRecordings
                        ? "Loading…"
                        : `Load More (${Math.max(0, recordingsTotal - Math.min(visibleCount, recordings.length))} remaining)`}
                    </Button>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
      </>)}

      {/* Transcription dialog (compact view tap target) */}
      {(() => {
        const rec = transcriptDialogFile ? recordings.find(r => r.filename === transcriptDialogFile) : null;
        const open = !!rec;
        return (
          <Dialog open={open} onOpenChange={(v) => { if (!v) setTranscriptDialogFile(null); }}>
            <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-md">
              {rec && (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 flex-wrap text-base">
                      <span>{rec.user_id}</span>
                      {rec.channel_name && (
                        <Badge variant="outline" className="text-xs bg-blue-900/50 border-blue-700 text-blue-300 font-normal">
                          {rec.channel_name}
                        </Badge>
                      )}
                    </DialogTitle>
                    <DialogDescription className="text-slate-400 text-xs">
                      {formatDateLong(rec.date)} · {formatTime(rec.time)} · {formatFileSize(rec.size)}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="py-2 min-h-[60px]">
                    {rec.transcription ? (
                      <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">
                        {rec.transcription}
                      </p>
                    ) : (
                      <div className="flex flex-col items-center gap-3 py-3 text-center">
                        <p className="text-sm text-slate-400">Not yet transcribed</p>
                        <Button
                          size="sm"
                          onClick={() => transcribeRecording(rec.filename)}
                          disabled={transcribingFile === rec.filename}
                        >
                          {transcribingFile === rec.filename ? (
                            <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Transcribing...</>
                          ) : (
                            <><MessageSquare className="w-4 h-4 mr-1" /> Transcribe now</>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                  <DialogFooter className="flex-row justify-between gap-2 sm:justify-between">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => playRecording(rec.filename)}
                    >
                      {playingFile === rec.filename && isPlaying ? (
                        <><Pause className="w-4 h-4 mr-1" /> Pause</>
                      ) : (
                        <><Play className="w-4 h-4 mr-1" /> Play</>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadRecording(rec.filename)}
                    >
                      <Download className="w-4 h-4 mr-1" /> Download
                    </Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>
        );
      })()}
        </TabsContent>
      </Tabs>
      {/* Merge progress overlay — sits above everything while a merge runs.
          Polls the backend every 500ms; bar fills as clips finish downloading. */}
      {mergeProgress && (
        <div className="fixed inset-0 bg-black/70 z-[9000] flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-6 w-full max-w-sm">
            <div className="flex items-center gap-2 mb-3">
              <Loader2 className="w-4 h-4 animate-spin text-green-400" />
              <span className="text-sm font-semibold text-slate-100">
                {mergeProgress.state === "merging" ? "Stitching audio…"
                  : mergeProgress.state === "starting" ? "Starting…"
                  : `Downloading clips`}
              </span>
            </div>
            <div className="h-2 bg-slate-800 rounded overflow-hidden">
              <div
                className="h-full bg-green-500 transition-all duration-200"
                style={{
                  width: `${mergeProgress.total
                    ? Math.min(100, Math.round((mergeProgress.downloaded / mergeProgress.total) * 100))
                    : 0}%`,
                }}
              />
            </div>
            <div className="mt-2 text-xs text-slate-400 text-right">
              {mergeProgress.downloaded} / {mergeProgress.total}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
